/**
 * Create Lookalike Modal
 * Story: E007-S027 Audience Manager & Targeting Optimization
 */

import React, { useState } from 'react';
import { useCreateLookalike } from '../../lib/audiencesApi';
import type { Audience, Platform, CreateLookalikeRequest } from '../../types/audiences';
import { formatAudienceSize, PLATFORM_INFO } from '../../types/audiences';

interface Props {
  seedAudience: Audience;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateLookalikeModal: React.FC<Props> = ({ seedAudience, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateLookalikeRequest>({
    name: `${seedAudience.name} - Lookalike`,
    similarity_percent: 2,
    platform: seedAudience.platform,
    location: 'US',
  });

  const createLookalike = useCreateLookalike();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createLookalike.mutateAsync({
        seedAudienceId: seedAudience.id,
        request: formData,
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create lookalike:', error);
    }
  };

  const similarityOptions = [
    { value: 1, label: '1%', description: 'Most similar, smallest reach' },
    { value: 2, label: '2%', description: 'High quality, good reach' },
    { value: 3, label: '3%', description: 'Balanced quality & reach' },
    { value: 5, label: '5%', description: 'Broad reach, moderate quality' },
    { value: 10, label: '10%', description: 'Maximum reach, lowest quality' },
  ];

  // Estimate lookalike size based on similarity
  const estimateSize = (percent: number): number => {
    const baseMultiplier = {
      1: 5,
      2: 12,
      3: 20,
      5: 35,
      10: 80,
    }[percent] || 10;
    return (seedAudience.size || 100000) * baseMultiplier;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create Lookalike Audience
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Seed Audience Info */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
            <div className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-1">
              Source Audience
            </div>
            <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {seedAudience.name}
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                PLATFORM_INFO[seedAudience.platform].color === 'blue' ? 'bg-blue-100 text-blue-700' :
                PLATFORM_INFO[seedAudience.platform].color === 'red' ? 'bg-red-100 text-red-700' :
                'bg-pink-100 text-pink-700'
              }`}>
                {PLATFORM_INFO[seedAudience.platform].name}
              </span>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {formatAudienceSize(seedAudience.size)} users
            </div>
          </div>

          {/* Lookalike Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Lookalike Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target Platform
            </label>
            <div className="flex gap-3">
              {(['meta', 'google', 'tiktok'] as Platform[]).map((platform) => (
                <button
                  key={platform}
                  type="button"
                  onClick={() => setFormData({ ...formData, platform })}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                    formData.platform === platform
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium text-gray-900 dark:text-white">
                    {PLATFORM_INFO[platform].name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Similarity Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Similarity Level
            </label>
            <div className="space-y-2">
              {similarityOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.similarity_percent === option.value
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="similarity"
                    checked={formData.similarity_percent === option.value}
                    onChange={() => setFormData({ ...formData, similarity_percent: option.value })}
                    className="text-purple-600"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">{option.label}</span>
                      <span className="text-sm text-gray-500">
                        ~{formatAudienceSize(estimateSize(option.value))}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{option.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Target Location
            </label>
            <select
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="US">United States</option>
              <option value="UK">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="MX">Mexico</option>
              <option value="BR">Brazil</option>
            </select>
          </div>

          {/* Estimated Size */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Estimated Reach:</span>
              <span className="text-xl font-bold text-purple-600 dark:text-purple-400">
                ~{formatAudienceSize(estimateSize(formData.similarity_percent))} people
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Based on {formData.similarity_percent}% similarity to seed audience of{' '}
              {formatAudienceSize(seedAudience.size)}.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.name || createLookalike.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {createLookalike.isPending && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              Create Lookalike
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
